import Controller from "./class.Controller.js";
import TransporterActions from "./class.TransporterActions.js";
import TransporterLog from "./class.TransporterLog.js";

const LOGGER = new TransporterLog({
	attributes: {
		name: "TransporterRunner",
		color: "yellow",
	},
});

/**
 * It's the Runner, yo.
 * TODO: Documentation
 */
export default class TransporterRunner extends Controller {
	/**
	 * @param {Object} options
	 * @param {*} options.mainController The main Transporter controller.
	 * @param {*} options.mainView The TransporterView controller.
	 * @param {*} options.layoutView The default TransporterViewLayout cotroller.
	 * @param {*} options.inputController The TransporterInput controller.
	 * @param {*} options.progress TBD
	 */
	constructor(options) {
		super(options);

		// This is called when the page first loads
		// Set up the ProgressBar and enable animation if the lesson is not complete.
		var enableAnimation = !this.progress || !this.progress.isComplete;

		if (AoPS.Grid && AoPS.Grid.ProgressBar) {
			this.progressBar = AoPS.Grid.ProgressBar;
			this.progressBar.enableAnimation(enableAnimation);
			this.progressBar.init(options.document.length - 1);
		}

		this.root = {
			type: "_root",
			body: options.document || [],
		};
		this.flow_chart = {};

		// Map pointers to a symbol.
		this.symbol = 0;
		this.symbolTable = {};
	}

	reset() {
		this.root = {
			type: "_root",
			body: this.mainController.document || [],
		};
		this.flow_chart = {};
		this.symbol = 0;
		this.symbolTable = {};

		// Reset progress bar if it exists
		if (this.progressBar) {
			this.progressBar.enableAnimation(true);
			this.progressBar.init(this.root.body.length - 1);
		}
	}

	/**
	 * Returns the symbol for a pointer from the symbol table.
	 *
	 * @param {*} pointer
	 * @param {number} context
	 */
	getPointerSymbol(pointer, context) {
		if (pointer) {
			var pointerStr = this.getPointerString(pointer, context);
			return this.symbolTable[pointerStr]
				? this.symbolTable[pointerStr]
				: (this.symbolTable[pointerStr] = this.symbol++);
		}
		return null;
	}

	/**
	 * Deterministically converts a pointer to a string.
	 * Because JSON.stringify is not deterministic on non-array objects.
	 *
	 * @param {*} pointer
	 * @param {number} context
	 */
	getPointerString(pointer, context) {
		var str = "";
		if (pointer) {
			for (let i = 0; i < pointer.length; i++) {
				let node = pointer[i];
				if (typeof node === "object") {
					node = [node.call, node.traverse].join("");
				}
				str += String(node);
			}
			str = [context, str].join("");
		}
		return str;
	}

	/**
	 * Returns an item from the document via its pointer.
	 *
	 * @param {*} pointer
	 */
	findItemByPointer(pointer) {
		return _.reduce(
			pointer,
			(carry, item) => {
				if (_.isObject(item)) {
					return carry;
				} else {
					return _.isObject(carry) || _.isArray(carry)
						? carry[item]
						: undefined;
				}
			},
			this.root
		);
	}

	/**
	 * Creates a flow traverse list.
	 *
	 * @param {*} pointer A pointer to start from.
	 * @param {*} item TODO:
	 * @param {*} flowList TODO:
	 */
	createFlowTraverseList(pointer, item, flowList) {
		var partial = [];
		_.each(flowList, (flowItem) => {
			if (_.isObject(flowItem) && flowItem.call) {
				partial.push({
					pointer: pointer.concat([flowItem]),
					action: flowItem.call,
				});
			} else if (_.isObject(flowItem) && flowItem.traverse) {
				_.each(
					_.isArray(item[flowItem.traverse])
						? item[flowItem.traverse]
						: [item[flowItem.traverse]],
					(sub, index) => {
						if (_.isObject(sub)) {
							var sub_type = AoPS.Transporter.CompositeObjectTypes[sub.type];
							if (sub_type && sub_type.flow) {
								partial.push({
									pointer: pointer.concat([flowItem.traverse, index]),
								});
							}
						}
					}
				);
			}
		});
		return partial;
	}

	/**
	 * Traverses a flow traverse list from the top.
	 *
	 * @param {Object} options
	 * @param {*} options.traverseList
	 * @param {Function} options.onAdvance
	 * @param {Function} options.onBypass
	 * @param {*} options.$anchor
	 * @param {number} options.context
	 */
	traverseFlowTraverseListTop(options) {
		var {traverseList, onAdvance, onBypass, $anchor, context} = options;

		if (traverseList.length) {
			var top = _.first(traverseList);
			this.runByPointer({
				pointer: top.pointer,
				action: top.action,
				onAdvance: ($newAnchor) => {
					this.traverseFlowTraverseListTop({
						traverseList: traverseList.slice(1),
						onAdvance,
						onBypass,
						$anchor: $newAnchor,
						context,
					});
				},
				onBypass,
				$anchor,
				context,
			});
		} else {
			onAdvance($anchor);
		}
	}

	/**
	 * Creates a flow traverse list then traverses it.
	 * This is called whenever a bubble is rendered
	 * @param {Object} options
	 * @param {*} options.pointer
	 * @param {*} options.item
	 * @param {*} options.flow
	 * @param {Function} options.onAdvance
	 * @param {Function} options.onBypass
	 * @param {*} options.$anchor
	 * @param {number} options.context
	 */
	createAndTraverseFlow(options) {
		var {pointer, item, flow, onAdvance, onBypass, $anchor, context} = options;

		this.updateProgressBar(pointer);

		var traverseList = this.createFlowTraverseList(pointer, item, flow);

		this.traverseFlowTraverseListTop({
			traverseList,
			onAdvance,
			onBypass,
			$anchor,
			context,
		});
	}

	/**
	 * Update the progress in the progress bar
	 * @param {string[]} pointer
	 */
	updateProgressBar(pointer) {
		if (
			pointer[0] === "body" &&
			Number.isInteger(pointer[1]) &&
			this.progressBar
		) {
			// Get the last clicked item
			const currentStep = pointer[1];

			// Update the status
			this.progressBar.setProgress(currentStep);
		}
	}

	/**
	 * Runs a pointer/action.
	 *
	 * @param {Object} options
	 * @param {*} options.pointer
	 * @param {*} options.action
	 * @param {Function} options.onAdvance
	 * @param {Function} options.onBypass
	 * @param {*} options.$anchor
	 * @param {number} options.context
	 */
	runByPointer(options) {
		var {pointer, action, onAdvance, onBypass, $anchor, context} = options;

		// Updates to debug hotbar with the latest reference hash.
		if (this.mainController.debugger) {
			if (pointer.length >= 2) {
				this.mainController.debugger.setFakeGotoHotbar(pointer[1]);
			}
		}

		var item = this.findItemByPointer(pointer);
		if (_.isObject(item) && this.isNodeShown(item)) {
			var type = AoPS.Transporter.CompositeObjectTypes[item.type];
			if (type) {
				item = _.extend({}, type.default_object, item);
				if (action) {
					if (_.isFunction(TransporterActions[action])) {
						let layoutView = this.mainView.getContextLayout(context, $anchor);
						if (layoutView.$el.html() === "") {
							// If this is a fresh layout, we just did a GoTo.
							// So let's start appending to the layout element for this context.
							$anchor = undefined;
						}
						// eslint-disable-next-line no-unused-vars
						var actionController = new TransporterActions[action]({
							model: item,
							attributes: {
								mainController: this.mainController,
								runnerController: this,
								inputController: this.inputController,
								animationController: this.mainController.animationController,
								progress: this.progress,
								mainView: this.mainView,
								layoutView: layoutView,
								type: AoPS.Transporter.CompositeObjectTypes[item.type],
								pointer: pointer,
								context: context,
								onAdvance: onAdvance,
								onBypass: onBypass,
								$anchor: $anchor ? $anchor : layoutView.$el,
								method: $anchor ? "after" : "append",
								playthrough_id:
									this.mainController.analyticsController.playthrough_id,
							},
						});
					} else {
						LOGGER.error("Warning: Bypassing Unknown Action " + action);
						onAdvance($anchor);
					}
				} else if (type.flow) {
					this.createAndTraverseFlow({
						pointer,
						context,
						item,
						flow: type.flow,
						onAdvance,
						onBypass,
						$anchor,
					});
				} else {
					LOGGER.error("Error: type has no flow defined.", type);
					onAdvance($anchor);
				}
			} else {
				LOGGER.error("Error: item has no type.", item);
				onAdvance($anchor);
			}
		} else {
			onAdvance($anchor);
		}
	}

	/**
	 * Process the display_if
	 * If this returns true we show the node, if false we skip it
	 * @param {Object} item
	 * @returns
	 */
	isNodeShown(item) {
		let isNodeShown = true;

		if (item.display_if) {
			isNodeShown = this.mainController.transporterVariables.processValue(
				item.display_if
			);
		}

		return isNodeShown;
	}
}
